import { TuftPlan, TuftStroke, planPointAtLen } from "../lib/tuft";

const D2R = Math.PI / 180;

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function smoothstep(a: number, b: number, v: number) {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
}

function mix(a: number, b: number, f: number) {
  return a + (b - a) * f;
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function rugPath(ctx: CanvasRenderingContext2D) {
  rr(ctx, 105, 100, 790, 500, 46);
}

function drawStrokePartial(
  ctx: CanvasRenderingContext2D,
  s: TuftStroke,
  take: number,
  mode: "base" | "outline" | "highlight"
) {
  const pts = s.pts;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) {
    if (s.cum[i] <= take) {
      ctx.lineTo(pts[i].x, pts[i].y);
    } else {
      const seg = s.cum[i] - s.cum[i - 1] || 1;
      const f = (take - s.cum[i - 1]) / seg;
      ctx.lineTo(pts[i - 1].x + (pts[i].x - pts[i - 1].x) * f, pts[i - 1].y + (pts[i].y - pts[i - 1].y) * f);
      break;
    }
  }
  ctx.stroke();
  void mode;
}

export function renderTuftScene(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  plan: TuftPlan,
  p: number,
  dpr: number,
  quality = 1
) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);

  const m = smoothstep(0.9, 1.0, p); // 0 = working angle, 1 = front-facing finished
  const zoom = 1 + 0.035 * m;
  const s = Math.min(W / 1000, H / 700) * zoom;
  const ox = (W - 1000 * s) / 2;
  const oy = (H - 700 * s) / 2;

  // Parallelogram basis: angled while working, straight when finished.
  const tlx = mix(78, 50, m);
  const tly = mix(86, 52, m);
  const trx = mix(922, 950, m);
  const try_ = mix(64, 52, m);
  const blx = mix(66, 50, m);
  const bly = mix(638, 648, m);
  const bx = (trx - tlx) / 1000;
  const by = (try_ - tly) / 1000;
  const cx = (blx - tlx) / 700;
  const cy = (bly - tly) / 700;

  const applyScene = () =>
    ctx.setTransform(bx * s * dpr, by * s * dpr, cx * s * dpr, cy * s * dpr, (ox + tlx * s) * dpr, (oy + tly * s) * dpr);
  const applyBase = () => ctx.setTransform(s * dpr, 0, 0, s * dpr, ox * dpr, oy * dpr);

  const mapPt = (x: number, y: number): [number, number] => [tlx + bx * x + cx * y, tly + by * x + cy * y];

  applyScene();

  // Finished-rug drop shadow
  if (m > 0.01) {
    ctx.save();
    ctx.fillStyle = `rgba(38,40,34,${0.16 * m})`;
    rugPath(ctx);
    ctx.filter = "none";
    ctx.translate(0, 10 * m);
    ctx.fill();
    ctx.restore();
  }

  // Fabric
  ctx.save();
  ctx.beginPath();
  const fab: Array<[number, number]> = [
    [40, 55],
    [960, 55],
    [960, 645],
    [40, 645],
  ];
  ctx.moveTo(fab[0][0], fab[0][1]);
  for (let i = 1; i < 4; i++) ctx.lineTo(fab[i][0], fab[i][1]);
  ctx.closePath();
  ctx.fillStyle = "#F3ECD9";
  ctx.fill();
  ctx.clip();

  // Weave texture
  ctx.strokeStyle = "rgba(38,40,34,0.05)";
  ctx.lineWidth = 1;
  const weaveStep = quality < 1 ? 14 : 9;
  ctx.beginPath();
  for (let x = 44; x < 960; x += weaveStep) {
    ctx.moveTo(x, 55);
    ctx.lineTo(x, 645);
  }
  for (let y = 59; y < 645; y += weaveStep) {
    ctx.moveTo(40, y);
    ctx.lineTo(960, y);
  }
  ctx.stroke();
  ctx.restore();

  // Design outline (transferred pattern)
  const outlineA = clamp01((p - 0.02) / 0.06) * (1 - smoothstep(0.93, 1, p));
  if (outlineA > 0.01) {
    ctx.save();
    ctx.setLineDash([5, 8]);
    ctx.strokeStyle = `rgba(38,40,34,${0.35 * outlineA})`;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    for (const st of plan.strokes) {
      if (st.width >= 28) continue; // fill rows are stitched, not outlined
      ctx.beginPath();
      ctx.moveTo(st.pts[0].x, st.pts[0].y);
      for (let i = 1; i < st.pts.length; i++) ctx.lineTo(st.pts[i].x, st.pts[i].y);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Stitches
  const bp = clamp01((p - 0.08) / 0.84);
  let remaining = bp * plan.totalLen;

  ctx.save();
  rugPath(ctx);
  ctx.clip();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const st of plan.strokes) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, st.len);
    remaining -= take;

    ctx.strokeStyle = st.color;
    ctx.lineWidth = st.width;
    drawStrokePartial(ctx, st, take, "base");

    ctx.save();
    ctx.translate(-1.6, -2.6);
    ctx.strokeStyle = st.light;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = st.width * 0.34;
    drawStrokePartial(ctx, st, take, "highlight");
    ctx.restore();

    ctx.fillStyle = st.dark;
    for (const d of st.dots) {
      if (d.t > take) break;
      if (quality < 1 && (d.t as number) % 14 > 7) continue;
      ctx.beginPath();
      ctx.ellipse(d.x, d.y, d.r, d.r * 0.72, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = st.light;
    for (let i = st.dots.length - 1; i >= 0; i--) {
      const d = st.dots[i];
      if (d.t > take) continue;
      if (d.t < take - 300) break;
      ctx.beginPath();
      ctx.ellipse(d.x + 1.4, d.y - 1.2, d.r * 0.8, d.r * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // Fuzz pass on the finished rug
  if (m > 0.02) {
    ctx.save();
    rugPath(ctx);
    ctx.clip();
    ctx.globalAlpha = 0.55 * m;
    for (const f of plan.fuzz) {
      ctx.fillStyle = f.c;
      ctx.beginPath();
      ctx.ellipse(f.x, f.y, f.r, f.r * 0.72, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  // Frame
  const frameA = 1 - m;
  if (frameA > 0.01) {
    ctx.save();
    ctx.globalAlpha = frameA;
    ctx.fillStyle = "#7B5E3F";
    ctx.fillRect(-12, 6, 1024, 42);
    ctx.fillRect(-12, 652, 1024, 42);
    ctx.fillRect(6, 6, 34, 688);
    ctx.fillRect(960, 6, 34, 688);
    ctx.fillStyle = "#5C4430";
    ctx.fillRect(-12, 44, 1024, 6);
    ctx.fillRect(-12, 650, 1024, 6);
    ctx.fillStyle = "#B79A61";
    for (const [px, py] of [
      [23, 27],
      [977, 27],
      [23, 673],
      [977, 673],
    ]) {
      ctx.beginPath();
      ctx.arc(px, py, 11, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // Tufting gun
  const gunVisible = bp > 0 && bp < 1 && p < 0.945;
  if (gunVisible) {
    const at = planPointAtLen(plan, bp * plan.totalLen);
    if (at) {
      applyBase();
      const [gx, gy] = mapPt(at.x, at.y);
      const dirX = bx * Math.cos(at.angle) + cx * Math.sin(at.angle);
      const dirY = by * Math.cos(at.angle) + cy * Math.sin(at.angle);
      const dl = Math.hypot(dirX, dirY) || 1;
      const ang = Math.atan2(dirY, dirX);

      // trailing yarn strand to recent stitches
      const behind = planPointAtLen(plan, Math.max(0, bp * plan.totalLen - 240));
      if (behind) {
        const [bxp, byp] = mapPt(behind.x, behind.y);
        ctx.strokeStyle = "#EFE6CB";
        ctx.lineWidth = 3.4 * s * 0.9;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(gx - Math.cos(ang) * 40 * s, gy - Math.sin(ang) * 40 * s + 8 * s);
        ctx.quadraticCurveTo((gx + bxp) / 2, (gy + byp) / 2 + 26 * s, bxp, byp);
        ctx.stroke();
      }

      ctx.save();
      ctx.translate(gx, gy);
      ctx.rotate(ang);
      ctx.scale(1.3, 1.3);
      // shadow
      ctx.fillStyle = "rgba(38,40,34,0.16)";
      ctx.beginPath();
      ctx.ellipse(-6 * s, 20 * s, 44 * s, 9 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      // needle
      ctx.fillStyle = "#B79A61";
      ctx.fillRect(0, -2.1 * s, 26 * s, 4.2 * s);
      ctx.beginPath();
      ctx.moveTo(26 * s, -3.4 * s);
      ctx.lineTo(33 * s, 0);
      ctx.lineTo(26 * s, 3.4 * s);
      ctx.closePath();
      ctx.fill();
      // body
      rr(ctx, -58 * s, -15 * s, 62 * s, 30 * s, 6 * s);
      ctx.fillStyle = "#2E312B";
      ctx.fill();
      ctx.fillStyle = "#3C4038";
      ctx.fillRect(-58 * s, -15 * s, 62 * s, 9 * s);
      ctx.fillStyle = "#183E35";
      ctx.fillRect(-58 * s, 5 * s, 62 * s, 4 * s);
      // yarn feed
      ctx.beginPath();
      ctx.arc(-32 * s, -18 * s, 4.4 * s, 0, Math.PI * 2);
      ctx.fillStyle = "#EFE6CB";
      ctx.fill();
      // handle
      ctx.save();
      ctx.translate(-50 * s, 12 * s);
      ctx.rotate(0.32);
      rr(ctx, -6 * s, 0, 13 * s, 26 * s, 4 * s);
      ctx.fillStyle = "#742F40";
      ctx.fill();
      ctx.restore();
      ctx.restore();
    }
  }

  applyBase();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  void D2R;
}
